import React from "react";
import {
  useTable,
  useGlobalFilter,
  usePagination,
  useSortBy,
} from "react-table";
import { Row, Col, Button } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";

function getBbrIdNumber(bbrId) {
  const match = String(bbrId || "").match(/(\d+)/);
  return match ? Number(match[1]) : 0;
}

function getUserNewestScore(user) {
  if (user?.createdDate) {
    const time = new Date(user.createdDate).getTime();
    if (!Number.isNaN(time)) {
      return time;
    }
  }

  return getBbrIdNumber(user?.bbrId);
}

const UsersDataTable = ({ columns, data, onDelete, onRestore, deleteMode = 'soft' }) => {
  const navigate = useNavigate();

  const sortedData = React.useMemo(() => {
    if (!data || data.length === 0) return [];

    return [...data].sort(
      (a, b) => getUserNewestScore(b) - getUserNewestScore(a)
    );
  }, [data]);

  const sortTypes = React.useMemo(
    () => ({
      bbrIdNumeric: (rowA, rowB, columnId) =>
        getBbrIdNumber(rowA.values[columnId]) -
        getBbrIdNumber(rowB.values[columnId]),
    }),
    []
  );

  const enrichedColumns = React.useMemo(() => {
    return columns.map((col) => {
      let nextCol = { ...col };

      if (col.accessor === "bbrId") {
        nextCol = { ...nextCol, sortType: "bbrIdNumeric" };
      }

      if (col.accessor === "action" && (onDelete || onRestore)) {
        return {
          ...nextCol,
          disableSortBy: true,
          Cell: ({ row }) => (
            <div className="d-flex gap-2">
              {deleteMode === "permanent" && onRestore && (
                <button
                  className="btn btn-success btn-sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    onRestore(row.original.userId, e);
                  }}
                >
                  Restore
                </button>
              )}
              {onDelete && (
                <button
                  className={`btn btn-sm ${
                    deleteMode === "permanent" ? "btn-dark" : "btn-danger"
                  }`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(row.original.userId, e);
                  }}
                >
                  {deleteMode === "permanent"
                    ? "Delete Permanently"
                    : "Delete"}
                </button>
              )}
            </div>
          ),
        };
      }

      return nextCol;
    });
  }, [columns, onDelete, onRestore, deleteMode]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    state,
    setGlobalFilter,
    page,
    nextPage,
    previousPage,
    canNextPage,
    canPreviousPage,
  } = useTable(
    {
      columns: enrichedColumns,
      data: sortedData,
      sortTypes,
      initialState: {
        pageIndex: 0,
        pageSize: 10,
      },
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );
  const { pageIndex, globalFilter } = state;

  const handleRowClick = (userId) => {
    navigate(`/dashboard/user/${userId}`);
  };

  const exportToExcel = () => {
    const fileType =
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
    const fileExtension = ".xlsx";

    // Get the current page URL path
    const path = window.location.pathname;
    // Manipulate the path to get the desired filename format
    const fileName = path
      .split("/")
      .slice(2) // Remove the base domain and leading '/'
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1)) // Capitalize each part
      .join(" > ") // Replace '/' with '>'
      .replace(/\//g, " - ") // Replace '/' with ' - '
      .replace(/ /g, ""); // Remove spaces
    // Append a timestamp to the filename
    const timestamp = new Date()
      .toISOString()
      .replace(/:/g, "-")
      .replace("T", " ")
      .split(".")[0];
    const finalFileName = `${fileName} ${timestamp}${fileExtension}`;

    const formattedData = sortedData?.map(({ userId, ...rest }) => rest);

    const ws = XLSX.utils.json_to_sheet(formattedData);
    const wb = { Sheets: { data: ws }, SheetNames: ["data"] };
    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const excelBlob = new Blob([excelBuffer], { type: fileType });

    const downloadLink = document.createElement("a");
    downloadLink.href = URL.createObjectURL(excelBlob);
    downloadLink.download = finalFileName;
    downloadLink.click();
  };

  return (
    <div>
      <Row>
        <Col md={9}>
          <div>
            <div class="form-floating mb-3">
              <input
                type="text"
                value={globalFilter || ""}
                onChange={(e) => setGlobalFilter(e.target.value)}
                placeholder="Search..."
                className="form-control form-control-solid-bordered"
                aria-describedby="..."
                id="floatingInput"
              />
              <label for="floatingInput">Search</label>
            </div>
          </div>
        </Col>
        <Col md={3} className="d-flex align-items-center gap-2">
          <Button className="mb-3" onClick={exportToExcel}>
            Export Excel
          </Button>
        </Col>
      </Row>

      <table
        {...getTableProps()}
        style={{ borderSpacing: "0", width: "100%" }}
        className="table"
      >
        <thead>
          {headerGroups.map((headerGroup) => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column) => (
                <th
                  {...column.getHeaderProps(column.getSortByToggleProps())}
                  style={{
                    borderBottom: "1px solid black",
                    whiteSpace: "nowrap",
                  }}
                >
                  {column.render("Header")}
                  <span>
                    {column.isSorted
                      ? column.isSortedDesc
                        ? " ↓"
                        : " ↑"
                      : "↓↑"}
                  </span>
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {page.map((row) => {
            prepareRow(row);
            return (
              <tr
                style={{ cursor: "pointer" }}
                {...row.getRowProps()}
                onClick={() => handleRowClick(row.original.userId)}
              >
                {row.cells.map((cell) => (
                  <td
                    {...cell.getCellProps()}
                    style={{ padding: "10px", borderBottom: "1px solid black" }}
                  >
                    {cell.render("Cell")}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
      <div>
        <Row className="mt-2">
          <Col xs={6} className="d-flex align-items-center">
            <span className="m-2">
              Page <strong>{pageIndex + 1}</strong>{" "}
            </span>
          </Col>
          <Col xs={6} className="d-flex justify-content-end">
            <button
              className={`btn ${
                canPreviousPage ? "btn-primary" : "btn-light"
              } m-1`}
              onClick={() => previousPage()}
              disabled={!canPreviousPage}
            >
              Previous Page
            </button>
            <button
              className={`btn ${canNextPage ? "btn-primary" : "btn-light"} m-1`}
              onClick={() => nextPage()}
              disabled={!canNextPage}
            >
              Next Page
            </button>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default UsersDataTable;
